import { useEffect, useRef } from "react";
import { X } from "lucide-react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional className for the dialog panel (defaults to max-w-lg) */
  className?: string;
}

export const Dialog = ({ open, onClose, children, className }: DialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-20 pb-4 overflow-y-auto">
      {/* Backdrop - covers full screen, header will be above it due to z-index */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className={
          className ??
          "relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[calc(100vh-5rem)] overflow-y-auto z-50 my-4"
        }
      >
        {children}
      </div>
    </div>
  );
};

interface DialogHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
}

export const DialogHeader = ({ children, onClose }: DialogHeaderProps) => {
  return (
    <div className="flex items-center justify-between p-6 border-b border-gray-200">
      <div className="flex-1">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close dialog"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

interface DialogTitleProps {
  children: React.ReactNode;
}

export const DialogTitle = ({ children }: DialogTitleProps) => {
  return <h2 className="text-xl font-semibold text-gray-900">{children}</h2>;
};

interface DialogContentProps {
  children: React.ReactNode;
}

export const DialogContent = ({ children }: DialogContentProps) => {
  return <div className="p-6">{children}</div>;
};

interface DialogFooterProps {
  children: React.ReactNode;
}

export const DialogFooter = ({ children }: DialogFooterProps) => {
  return (
    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
      {children}
    </div>
  );
};
